import { AiCallDimension, type AiCallOrder } from "@pax/api-contract/ai/calls/v1alpha1";
import { type AnimationEvent, type ReactNode, useMemo, useRef, useState } from "react";
import { Failure, Workspace } from "@/features/evals/components";
import { DownloadMenu, jsonPart } from "@/features/evals/DownloadMenu";
import { api, reachable } from "@/lib/api";
import { CallPage } from "./CallPage";
import { CallList } from "./Calls";
import { Chart, changeText, reading, Table } from "./Chart";
import { type CallFilter, type Dimension, isDimension, type Keep } from "./dimensions";
import { AddFilter, FilterChip, PromptChip } from "./Filters";
import {
  type ChartSpec,
  type Column,
  emptyTotals,
  isSplit,
  isView,
  measure,
  metricIds,
  metrics,
  type Split,
  splitGroups,
  splitLabel,
  splitPhrase,
  splitsByTokenKind,
  unpinnedSplit,
  type View,
  views,
} from "./measures";
import { defaultCharts, Profile } from "./Profile";
import { Picker } from "./pieces";
import {
  bucketName,
  type CallShow,
  clock,
  dayName,
  isCallShow,
  isCallSort,
  isRange,
  type Range,
  type Row,
  ranges,
  useQuestion,
  useTotalsBefore,
  windowOf,
} from "./queries";

/** The page's place, kept in its query so a link opens the same question. */
export interface ModelsLocation {
  measure?: View;
  groupBy?: Split;
  range?: Range;
  keep?: Keep;
  promptOver?: number;
  /** The charted column. */
  chart?: string;
  ascending?: boolean;
  callSort?: AiCallOrder;
  callShow?: CallShow;
  /** The profiles opened one inside the next, each as `dimension:value`. */
  open?: string[];
  call?: string;
}

const strings = (value: unknown) =>
  Array.isArray(value) && value.every(item => typeof item === "string") && value.length > 0
    ? (value as string[])
    : undefined;

function readKeep(value: unknown): Keep | undefined {
  if (!value || typeof value !== "object") return undefined;
  const keep: Keep = {};
  for (const [dimension, values] of Object.entries(value)) {
    const kept = strings(values);
    if (AiCallDimension.safeParse(dimension).success && kept) keep[dimension as Dimension] = kept;
  }
  return Object.keys(keep).length ? keep : undefined;
}

export function readModelsLocation(search: Record<string, unknown>): ModelsLocation {
  return {
    measure: isView(search.measure) ? search.measure : undefined,
    groupBy: isSplit(search.groupBy) ? search.groupBy : undefined,
    range: isRange(search.range) ? search.range : undefined,
    keep: readKeep(search.keep),
    promptOver: typeof search.promptOver === "number" ? search.promptOver : undefined,
    chart: typeof search.chart === "string" ? search.chart : undefined,
    ascending: search.ascending === true || undefined,
    callSort: isCallSort(search.callSort) ? search.callSort : undefined,
    callShow: isCallShow(search.callShow) ? search.callShow : undefined,
    open: strings(search.open)?.filter(step => readStep(step)),
    call: typeof search.call === "string" ? search.call : undefined,
  };
}

function readStep(step: string): { dimension: Dimension; value: string } | undefined {
  const colon = step.indexOf(":");
  const dimension = step.slice(0, colon);
  return colon > 0 && isDimension(dimension)
    ? { dimension, value: step.slice(colon + 1) }
    : undefined;
}

const csvCell = (value: string) =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** The table as it reads, one row per value of the split, every column the view has. */
function tableCsv(rows: Row[], columns: Column[]): string {
  const lines = [["Name", ...columns.map(column => column.label)]];
  for (const row of rows.flatMap(row => row.others ?? [row]))
    lines.push([
      row.label,
      ...columns.map(column => String(column.value(row.window, false) ?? "")),
    ]);
  return `${lines.map(line => line.map(csvCell).join(",")).join("\n")}\n`;
}

/* ── Screens on top of the page ─────────────────────────────────────── */

type Screen =
  | {
      kind: "profile";
      key: string;
      depth: number;
      dimension: Dimension;
      value: string;
      within: CallFilter;
    }
  | { kind: "call"; key: string; depth: number; callId: string };

/** The screen that just went back, held while it travels down the way it came. */
function useLeaving(screens: Screen[]) {
  const [previous, setPrevious] = useState(screens);
  const [leaving, setLeaving] = useState<Screen | null>(null);
  const keysOf = (list: Screen[]) => list.map(screen => screen.key).join("\n");
  if (keysOf(previous) !== keysOf(screens)) {
    const top = previous.at(-1);
    if (top && !screens.some(screen => screen.key === top.key)) setLeaving(top);
    setPrevious(screens);
  }
  return [leaving, () => setLeaving(null)] as const;
}

/** One screen on the stack. It comes up from the foot and goes back down the way it came. */
function Sheet({
  arrives,
  leaving,
  covered,
  onLeft,
  onBack,
  children,
}: {
  arrives: boolean;
  leaving: boolean;
  covered: boolean;
  onLeft: () => void;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <div
      inert={covered || leaving}
      className={`app-screen-top-corners fixed inset-0 z-30 overflow-hidden bg-screen shadow-[0_-12px_36px_rgb(0_0_0/0.22)] ${
        leaving ? "pile-page-out" : arrives ? "pile-page-in" : ""
      }`}
      onAnimationEnd={(event: AnimationEvent<HTMLDivElement>) => {
        if (leaving && event.target === event.currentTarget) onLeft();
      }}
    >
      <div className="absolute inset-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <Workspace back={onBack}>
          <div>{children}</div>
        </Workspace>
      </div>
    </div>
  );
}

/* ── The page ───────────────────────────────────────────────────────── */

const CALL_PAGE_SIZE = 20;
const firstColumn = (view: View) => views[view].columns[0] ?? measure("spend");

/**
 * Every model call Pax made, asked one question at a time: one measure, split one way, over one
 * stretch of time, for whichever calls the filters keep. Pressing a row opens its profile; the
 * last level is a single call.
 */
export function ModelsWorkspace({
  location,
  navigate,
}: {
  location: ModelsLocation;
  navigate: (next: ModelsLocation, options?: { replace?: boolean }) => void;
}) {
  const view = location.measure ?? "overview";
  const split = location.groupBy ?? "model";
  const range = location.range ?? "week";
  const keep = location.keep ?? {};
  const open = location.open ?? [];
  const filter: CallFilter = { keep, promptOver: location.promptOver };
  const window = useMemo(() => windowOf(range, Date.now()), [range]);

  const [hoveredBucket, setHoveredBucket] = useState<number | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [showOthers, setShowOthers] = useState(false);
  const [chartsByDimension, setChartsByDimension] = useState<
    Partial<Record<Dimension, ChartSpec[]>>
  >({});

  const update = (changes: ModelsLocation, replace = false) =>
    navigate({ ...location, ...changes }, { replace });

  const columns = views[view].columns;
  const chartedColumn = columns.find(column => column.id === location.chart) ?? firstColumn(view);
  const ascending = location.ascending ?? false;
  const question = useQuestion(filter, split, window, chartedColumn, false);
  const before = useTotalsBefore(filter, window);
  const shaped = question.shaped;
  const now =
    hoveredBucket === null ? shaped?.window : (shaped?.buckets[hoveredBucket] ?? emptyTotals());
  const bucketFrom = window.from + (hoveredBucket ?? 0) * window.bucketMs;
  const empty = shaped !== undefined && shaped.window.calls === 0;

  const changeFilter = (nextKeep: Keep, promptOver = location.promptOver) => {
    const cleaned = Object.fromEntries(
      Object.entries(nextKeep).filter(([, values]) => values && values.length > 0),
    ) as Keep;
    setHoveredRow(null);
    setShowOthers(false);
    update({
      keep: Object.keys(cleaned).length ? cleaned : undefined,
      promptOver,
      groupBy: unpinnedSplit(split, cleaned),
    });
  };
  const pickView = (id: View) => {
    setHoveredBucket(null);
    update({
      measure: id,
      chart: undefined,
      ascending: undefined,
      groupBy: split === "tokenKind" && !splitsByTokenKind(id) ? "none" : split,
    });
  };
  /** The first press charts a column; pressing the charted column again turns its sort around. */
  const chart = (id: string) => {
    if (id === chartedColumn.id) return update({ ascending: !ascending || undefined }, true);
    setHoveredBucket(null);
    update({ chart: id, ascending: undefined }, true);
  };

  const steps = open.flatMap(step => readStep(step) ?? []);
  const screens: Screen[] = [];
  let within = filter;
  steps.forEach((step, index) => {
    screens.push({
      kind: "profile",
      key: `profile:${open.slice(0, index + 1).join("|")}`,
      depth: index + 1,
      ...step,
      within,
    });
    within = { ...within, keep: { ...within.keep, [step.dimension]: [step.value] } };
  });
  if (location.call)
    screens.push({
      kind: "call",
      key: `call:${open.join("|")}:${location.call}`,
      depth: steps.length + 1,
      callId: location.call,
    });
  const [leaving, onLeft] = useLeaving(screens);
  const shown = leaving ? [...screens, leaving] : screens;
  const arrivedAtLoad = useRef(new Set(screens.map(screen => screen.key)));

  const openProfile = (depth: number, dimension: Dimension, value: string) =>
    update({ open: [...open.slice(0, depth), `${dimension}:${value}`], call: undefined });
  const openCall = (depth: number, callId: string) =>
    update({ open: open.slice(0, depth).length ? open.slice(0, depth) : undefined, call: callId });
  const back = () =>
    location.call
      ? update({ call: undefined })
      : update({ open: open.length > 1 ? open.slice(0, -1) : undefined });

  const filters = Object.entries(keep) as [Dimension, string[]][];

  return (
    <>
      <div inert={screens.length > 0}>
        <Workspace
          downloadMenu={
            <DownloadMenu
              title="models"
              name={`models-${view}-${split}-${range}`}
              initial={["table", "calls"]}
              choices={[
                {
                  key: "table",
                  label: "This table",
                  detail: `CSV of every column, ${splitPhrase(split)}`,
                },
                { key: "calls", label: "These calls", detail: "JSON of the latest 100 calls" },
              ]}
              load={async selected => [
                ...(selected.includes("table") && shaped
                  ? [{ name: "table.csv", data: tableCsv(shaped.rows, columns) }]
                  : []),
                ...(selected.includes("calls")
                  ? [
                      jsonPart(
                        "calls",
                        (
                          await reachable(
                            api.ai.calls.search({
                              filter: {
                                from: new Date(window.from).toISOString(),
                                to: new Date(window.to).toISOString(),
                                keep,
                                ...(location.promptOver
                                  ? { promptTokensOver: location.promptOver }
                                  : {}),
                              },
                              order: "latest",
                              limit: 100,
                            }),
                          )
                        ).items,
                      ),
                    ]
                  : []),
              ]}
            />
          }
        >
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Models</h1>

            <div className="mt-4 -ml-3 flex flex-wrap items-center text-sm text-muted-foreground">
              <Picker
                label={views[view].label}
                groups={[
                  [{ id: "overview", label: "Overview", selected: view === "overview" }],
                  metricIds.map(id => ({ id, label: metrics[id].label, selected: id === view })),
                ]}
                onPick={id => pickView(id as View)}
              />
              grouped by
              <Picker
                label={splitLabel(split)}
                groups={splitGroups(view, split, keep)}
                onPick={id => {
                  setShowOthers(false);
                  update({ groupBy: id as Split });
                }}
              />
              over
              <Picker
                label={ranges[range].label}
                groups={[
                  (Object.keys(ranges) as Range[]).map(id => ({
                    id,
                    label: ranges[id].label,
                    selected: id === range,
                  })),
                ]}
                onPick={id => {
                  setHoveredBucket(null);
                  update({ range: id as Range });
                }}
              />
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {filters.map(([dimension, values]) => (
                <FilterChip
                  key={dimension}
                  dimension={dimension}
                  values={values}
                  filter={filter}
                  window={window}
                  onChange={next => changeFilter({ ...keep, [dimension]: next })}
                />
              ))}
              {location.promptOver && (
                <PromptChip
                  promptOver={location.promptOver}
                  onChange={tokens => changeFilter(keep, tokens)}
                />
              )}
              <span className={filters.length || location.promptOver ? "" : "-ml-2.5"}>
                <AddFilter
                  filter={filter}
                  window={window}
                  onPromptOver={tokens => changeFilter(keep, tokens)}
                  onAdd={(dimension, value) => {
                    const current = keep[dimension] ?? [];
                    changeFilter({
                      ...keep,
                      [dimension]: current.includes(value)
                        ? current.filter(chosen => chosen !== value)
                        : [...current, value],
                    });
                  }}
                />
              </span>
            </div>

            <div className="mt-4">
              <Failure error={question.error} />
            </div>
            {question.isPending && (
              <p className="mt-5 text-sm text-muted-foreground">Reading calls…</p>
            )}

            {empty && (
              <div className="mt-8 rounded-2xl bg-foreground/[0.03] px-6 py-12 text-center">
                <p className="text-sm font-medium">No model calls over {ranges[range].label}</p>
                <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                  {filters.length || location.promptOver
                    ? "Nothing matches these filters. Remove one, or pick a longer stretch of time."
                    : "Every call an agent makes through the AI gateway shows up here."}
                </p>
              </div>
            )}

            {shaped && !empty && now && (
              <>
                {(view === "overview" || chartedColumn.id !== columns[0]?.id) && (
                  <p className="mt-1 text-sm text-muted-foreground">{chartedColumn.label}</p>
                )}
                <p className="mt-0.5 text-4xl font-semibold tracking-tight tabular-nums">
                  {reading(chartedColumn, now, false)}
                </p>
                <p className="mt-1 h-5 truncate text-sm text-muted-foreground tabular-nums">
                  {hoveredBucket !== null
                    ? bucketName(bucketFrom, bucketFrom + window.bucketMs, range)
                    : changeText(chartedColumn, shaped.window, before.data, range)}
                </p>

                <Chart
                  rows={shaped.rows}
                  metric={chartedColumn}
                  slowest={false}
                  hoveredBucket={hoveredBucket}
                  onHoverBucket={setHoveredBucket}
                  hoveredRow={hoveredRow}
                />
                <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
                  <span>
                    {range === "month" || range === "week"
                      ? dayName(new Date(window.from))
                      : clock(new Date(window.from))}
                  </span>
                  <span>Now</span>
                </div>

                <Table
                  rows={shaped.rows}
                  columns={columns}
                  charted={chartedColumn.id}
                  ascending={ascending}
                  onChart={chart}
                  bucket={hoveredBucket}
                  tokenSplit={split === "tokenKind"}
                  showOthers={showOthers}
                  onToggleOthers={() => setShowOthers(current => !current)}
                  onHoverRow={setHoveredRow}
                  onOpen={(dimension, value) => openProfile(0, dimension, value)}
                />

                <CallList
                  filter={filter}
                  window={window}
                  sort={location.callSort ?? "latest"}
                  show={location.callShow ?? "all"}
                  onSort={sort => update({ callSort: sort === "latest" ? undefined : sort }, true)}
                  onShow={show => update({ callShow: show === "all" ? undefined : show }, true)}
                  pageSize={CALL_PAGE_SIZE}
                  onOpen={call => openCall(0, call.callId)}
                />
              </>
            )}
          </div>
        </Workspace>
      </div>

      {shown.map((screen, index) => (
        <Sheet
          key={screen.key}
          arrives={!arrivedAtLoad.current.has(screen.key)}
          leaving={screen === leaving}
          covered={index < screens.length - 1}
          onLeft={onLeft}
          onBack={back}
        >
          {screen.kind === "call" ? (
            <CallPage
              callId={screen.callId}
              onOpenCall={callId => openCall(screen.depth - 1, callId)}
            />
          ) : (
            <Profile
              dimension={screen.dimension}
              value={screen.value}
              within={screen.within}
              window={window}
              charts={
                chartsByDimension[screen.dimension] ??
                defaultCharts({ ...screen.within.keep, [screen.dimension]: [screen.value] })
              }
              onCharts={charts =>
                setChartsByDimension(current => ({ ...current, [screen.dimension]: charts }))
              }
              onRange={next => update({ range: next })}
              onOpen={(dimension, value) => openProfile(screen.depth, dimension, value)}
              onOpenCall={call => openCall(screen.depth, call.callId)}
            />
          )}
        </Sheet>
      ))}
    </>
  );
}
